/**
 * PrescribingPage.tsx
 *
 * ScriptSure's practice-level screens, for accounts that prescribe through
 * ScriptSure (DAW): Messages across every prescriber the user may see, pending
 * prescriptions, Reports, the Audit Log, and the full ScriptSure application,
 * where prescribers sign up for controlled-substance (EPCS) prescribing and
 * two-step sign-in. DAW requires each to be reachable without opening a
 * patient first (plan R1). The route and the nav entry both need an explicitly
 * active `daw` module (App.tsx, navigation.config.ts).
 *
 * Nothing is asked of the server when the page loads. Each click asks for that
 * one screen (POST /prescriptions/daw/workspace), because every open re-scopes
 * the user's one shared ScriptSure session, so an address issued earlier can
 * already be running under another practice or prescriber (plan KTD4). The
 * window opens only on a ready answer. A needsSelection answer shows the
 * practice/prescriber picker, and Continue asks for the same screen again
 * with both ids. That choice is kept while the page stays mounted, so the next
 * screen opens under it without asking again.
 */
import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AppWindow,
  BarChart2,
  Clock,
  MessageSquare,
  ScrollText,
  type LucideIcon,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { ScopePicker, type ScopeChoice } from '@/components/scriptsure/ScopePicker';
import { ScriptSureErrorNotice, errorViewFor } from '@/components/scriptsure/ScriptSureError';
import {
  ScriptSureWindow,
  scriptSureWindowLabel,
  type OpenScriptSureWindow,
} from '@/components/scriptsure/ScriptSureWindow';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import {
  DAW_PRESCRIPTION_LISTS,
  getDawErrorCode,
  openDawWorkspace,
  type DawSessionSelection,
  type DawWorkspaceBody,
  type DawWorkspaceWindow,
} from '@/services/prescriptions.service';

/** Listed before the full application, which carries a line of its own. */
const SCREENS: { kind: DawWorkspaceWindow; icon: LucideIcon }[] = [
  { kind: 'messages', icon: MessageSquare },
  { kind: 'pending', icon: Clock },
  { kind: 'reports', icon: BarChart2 },
  { kind: 'audit_log', icon: ScrollText },
];

interface ScreenButtonProps {
  kind: DawWorkspaceWindow;
  icon: LucideIcon;
  /** This screen's open is the one in flight. */
  opening: boolean;
  disabled: boolean;
  onOpen: (kind: DawWorkspaceWindow) => void;
  describedBy?: string;
}

function ScreenButton({ kind, icon: Icon, opening, disabled, onOpen, describedBy }: ScreenButtonProps) {
  return (
    <Button
      variant="secondary"
      leftIcon={<Icon className="h-4 w-4" />}
      loading={opening}
      disabled={disabled}
      onClick={() => onOpen(kind)}
      aria-describedby={describedBy}
    >
      {scriptSureWindowLabel(kind)}
    </Button>
  );
}

export function PrescribingPage() {
  const queryClient = useQueryClient();
  const appNoteId = useId();
  /** The user's last explicit practice/prescriber choice. */
  const [scope, setScope] = useState<ScopeChoice | null>(null);
  const [opened, setOpened] = useState<OpenScriptSureWindow | null>(null);
  /** The screen a needsSelection answer interrupted, with that answer's options. */
  const [awaitingScope, setAwaitingScope] = useState<{
    kind: DawWorkspaceWindow;
    selection: DawSessionSelection;
  } | null>(null);

  const mutation = useMutation({
    mutationFn: (body: DawWorkspaceBody) => openDawWorkspace(body),
  });

  function open(kind: DawWorkspaceWindow, choice: ScopeChoice | null) {
    setAwaitingScope(null);
    mutation.mutate(
      { window: kind, ...(choice ?? {}) },
      {
        onSuccess: (result) => {
          if (result.needsSelection) {
            setAwaitingScope({ kind, selection: result });
            return;
          }
          setOpened({ kind, url: result.iframeUrl });
        },
        onError: (err) => {
          // A kept choice the server no longer accepts would fail every screen
          // the same way, so it goes, and the next try lets the server choose
          // or ask again.
          if (choice && getDawErrorCode(err) === 'DAW_BAD_SCOPE') setScope(null);
        },
      },
    );
  }

  function handleContinue(choice: ScopeChoice) {
    if (!awaitingScope) return;
    setScope(choice);
    open(awaitingScope.kind, choice);
  }

  const pendingKind = mutation.isPending ? mutation.variables?.window : undefined;
  const failedKind = mutation.isError ? mutation.variables?.window : undefined;
  const errorView =
    mutation.isError && failedKind
      ? errorViewFor(mutation.error, () => open(failedKind, scope))
      : null;

  const openUnderScope = (kind: DawWorkspaceWindow) => open(kind, scope);

  return (
    <div className="mx-auto max-w-6xl space-y-8 px-6 py-6 md:px-8 lg:px-10">
      <PageHeader title="Prescribing" />

      <Card className="border-slate-200 bg-white">
        <CardBody className="space-y-4">
          <div className="flex flex-wrap gap-3">
            {SCREENS.map(({ kind, icon }) => (
              <ScreenButton
                key={kind}
                kind={kind}
                icon={icon}
                opening={pendingKind === kind}
                disabled={mutation.isPending}
                onOpen={openUnderScope}
              />
            ))}
          </div>

          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
            <ScreenButton
              kind="app"
              icon={AppWindow}
              opening={pendingKind === 'app'}
              disabled={mutation.isPending}
              onOpen={openUnderScope}
              describedBy={appNoteId}
            />
            <p id={appNoteId} className="text-sm text-slate-600">
              Sign up for controlled-substance prescribing and set up two-step sign-in here.
            </p>
          </div>

          {awaitingScope && (
            <ScopePicker selection={awaitingScope.selection} onContinue={handleContinue} />
          )}

          {errorView && <ScriptSureErrorNotice view={errorView} />}
        </CardBody>
      </Card>

      <ScriptSureWindow
        opened={opened}
        onClose={() => setOpened(null)}
        // A practice-level screen can act on any patient's prescriptions (a
        // renewal answered in Messages, say), so every patient's cached
        // prescription list is marked stale for its next showing.
        onSignal={() => void queryClient.invalidateQueries({ queryKey: DAW_PRESCRIPTION_LISTS })}
      />
    </div>
  );
}
